/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { useReactiveVar } from '@apollo/client';
import { ScrollablePane } from '@fluentui/react/lib/ScrollablePane';
import { Sticky, StickyPositionType } from '@fluentui/react/lib/Sticky';
import * as Teams from '@microsoft/teams-js';
import { useConst } from '@uifabric/react-hooks';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { availableFlowEnvironments } from '../../apollo/reactiveVariables/FlowEnvironmentVariables';
import { isFilterMenuOpen, isSearchFilterOpen } from '../../apollo/reactiveVariables/MobileApprovalsFilterVariables';
import { isEnvironmentPickerOpen } from '../../apollo/reactiveVariables/UIVariables';
import { Paths } from '../../common/enums/Paths';
import ApprovalsListProvider from '../../context/ApprovalsListContext/ApprovalsListProvider';
import ESignApprovalsListProvider from '../../context/ESignApprovalsListContext/ESignApprovalsListProvider';
import { useListDataQuery } from '../../data/client/queries/ListData.generated';
import { ApprovalRequestType } from '../../data/types.generated';
import { EntryPoint, PanelRegion, ScenarioName } from '../../logger';
import { getPlatformIcon, PlatformIconName } from '../../resources/icons/Icons';
import { DeepLinkType } from '../../service/DeepLinkManager/IDeepLinkManager';
import { ApprovalCreator } from '../../service/gen';
import { isEntryMenuEnabled } from '../../utilities/ECSConfigUtils';
import { getProviderNameLocale } from '../../utilities/EsignUtils';
import { getAppHost } from '../../utilities/TeamsUtils';
import { stringFormat } from '../../utilities/Utils';
import { ApprovalsAppCommandBar, ApprovalsAppList } from '../../views/components';
import { ApprovalsAppFilter } from '../../views/components/ApprovalsAppFilter';
import { ApprovalsUserRoleSelector } from '../../views/components/ApprovalsRoleSelector';
import { EsignProviderPageContainer } from '../../views/Esign/EsignProviderPage/EsignProviderPage.container';
import { eSignLogout, useShowESignLogoutButton } from '../../views/Esign/utils/Authentication/LogoutUtils';
import { ConfirmationDialog, ConfirmationDialogProps } from '../components/MobileConfirmationDialog';
import { AlphaLayer, AlphaLayerType } from '../components/MobileFilter/AlphaLayer';

export const MobileApprovalsApp: React.FC = (): JSX.Element => {
    getAppHost()
        .getLogger()
        .useCreateScenarioEffect(
            ScenarioName.PersonalApp,
            EntryPoint.PersonalApp,
            'appBar',
            PanelRegion.Main,
            'approvals'
        );

    const comesFromNotification =
        getAppHost().getDeepLinkManager().getDeepLinkType() !== DeepLinkType.Empty &&
        getAppHost().getDeepLinkManager().isDeepLinkFresh();

    const newApprovalBtnId = 'newApproval';
    const eSignLogoutButtonId = 'logout';
    const filterBtnId = 'filter';
    const searchBtnId = 'search';

    const history = useHistory();
    const loc = getAppHost().getLocale();
    const theme = getAppHost().getTheme();

    const [confirmationDialogProps, setConfirmationDialogProps] = React.useState<ConfirmationDialogProps>(null);

    const {
        data: { listData },
    } = useListDataQuery();
    const environmentList = useReactiveVar(availableFlowEnvironments);
    const showESignLogoutButton = useShowESignLogoutButton(listData.approvalType);
    const searchFilterValue = useReactiveVar<boolean>(isSearchFilterOpen);
    const statusFilterValue = useReactiveVar<boolean>(isFilterMenuOpen);

    const isPickerOpen = useReactiveVar<boolean>(isEnvironmentPickerOpen);

    const isIOS = getAppHost().isIOS();

    const createApprovalBtn: Teams.menus.MenuItem = {
        id: newApprovalBtnId,
        title: loc.strings.new_approval_button_text,
        enabled: true,
        viewData: null,
        icon: getPlatformIcon(PlatformIconName.newIcon, theme),
        selected: false,
    };

    const eSignLogoutButton: Teams.menus.MenuItem = {
        id: eSignLogoutButtonId,
        title: loc.strings.log_out,
        enabled: true,
        viewData: null,
        icon: getPlatformIcon(PlatformIconName.logoutIcon, theme),
        selected: false,
    };

    const filterBtn: Teams.menus.MenuItem = {
        id: filterBtnId,
        title: loc.strings.filter_button_title,
        enabled: true,
        viewData: null,
        icon: getPlatformIcon(PlatformIconName.filterIcon, theme),
        selected: false,
    };

    const searchBtn: Teams.menus.MenuItem = {
        id: searchBtnId,
        title: loc.strings.filter_keyword_search,
        enabled: true,
        viewData: null,
        icon: getPlatformIcon(PlatformIconName.searchIcon, theme),
        selected: false,
    };

    const navigateToNewApproval = (): void => {
        history.push({
            pathname: Paths.BasePath + Paths.CreateApproval,
        });
    };

    const navigateToCreateRequstMenu = (): void => {
        const logger = getAppHost().getLogger();
        // Initialize create request menu scenario
        logger.createScenario(ScenarioName.CreateRequestMenu, EntryPoint.PersonalApp);
        history.push({
            pathname: Paths.BasePath + Paths.CreateRequestMenu,
            state: { showNaviBar: true },
        });
    };

    const isEsign =
        listData.approvalType === ApprovalRequestType.Syntex ||
        listData.approvalType === ApprovalRequestType.ESign ||
        listData.approvalType === ApprovalRequestType.Docusign;

    const ListProvider = isEsign ? ESignApprovalsListProvider : ApprovalsListProvider;

    const approvalsAppList = <ListProvider listData={listData}>{useConst(<ApprovalsAppList />)}</ListProvider>;

    React.useEffect((): void => {
        const teamsNavBarMenuHandler = (id: string): boolean => {
            isEnvironmentPickerOpen(false);
            if (id === filterBtnId) {
                isFilterMenuOpen(!statusFilterValue);
                isSearchFilterOpen(false);
            } else if (id === searchBtnId) {
                isSearchFilterOpen(!searchFilterValue);
                isFilterMenuOpen(false);
            } else if (id === eSignLogoutButtonId) {
                const dismiss = (): void => setConfirmationDialogProps(null);
                const provider = getProviderNameLocale(listData.approvalType);
                setConfirmationDialogProps({
                    isOpen: true,
                    showDarkOverlay: true,
                    title: loc.strings.sign_out,
                    subText: stringFormat(loc.strings.sign_out_warning, { provider }),
                    dismiss,
                    actions: [
                        {
                            text: loc.strings.action_cancel,
                            onClick: dismiss,
                        },
                        {
                            text: loc.strings.sign_out,
                            onClick: (): void => {
                                eSignLogout(listData.approvalType);
                                dismiss();
                            },
                            primary: true,
                        },
                    ],
                });
            } else {
                if (isEntryMenuEnabled()) {
                    navigateToCreateRequstMenu();
                } else {
                    navigateToNewApproval();
                }
            }
            return true;
        };
        // If the App was invoked by a notification we should not load the nav bar items,
        // otherwise a flickering will be noticed
        if (!comesFromNotification) {
            if (listData.approvalType === ApprovalRequestType.Basic) {
                getAppHost().setNavBarMenu([createApprovalBtn, searchBtn, filterBtn], teamsNavBarMenuHandler);
            } else if (
                listData.approvalType === ApprovalRequestType.ESign ||
                listData.approvalType === ApprovalRequestType.Docusign
            ) {
                const items = [createApprovalBtn];
                if (showESignLogoutButton) {
                    items.unshift(eSignLogoutButton);
                }

                getAppHost().setNavBarMenu(items, teamsNavBarMenuHandler);
            } else if (listData.approvalType === ApprovalRequestType.Syntex) {
                getAppHost().setNavBarMenu([searchBtn], teamsNavBarMenuHandler);
            } else {
                getAppHost().setNavBarMenu([], null);
            }
        }
    }, [listData, environmentList, filterBtn, searchBtn]);

    const alphaClick = (evt: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
        evt.stopPropagation();
        evt.nativeEvent.stopImmediatePropagation();
        isEnvironmentPickerOpen(false);
    };

    return (
        <div className='approvals-mobile-container'>
            {isPickerOpen && isIOS && <AlphaLayer onClick={alphaClick} type={AlphaLayerType.environmentPicker} />}
            <ScrollablePane>
                <Sticky stickyPosition={StickyPositionType.Header}>
                    <ApprovalsUserRoleSelector />
                    <ApprovalsAppCommandBar />
                </Sticky>
                <EsignProviderPageContainer
                    showBackButton={false}
                    showTrialPageButton={true}
                    approvalType={listData.approvalType}
                    approvalCreator={ApprovalCreator.PersonalApp}>
                    {approvalsAppList}
                </EsignProviderPageContainer>
            </ScrollablePane>
            <ApprovalsAppFilter />
            <ConfirmationDialog {...confirmationDialogProps} />
        </div>
    );
};
